import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import LoaderComponent from '@components/LoaderComponent';
import TeamMember from '@components/TeamMember';
import { toastError, toastSuccess } from '@components/Toast';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import ApiClient from '@lib/apiClient';

const AddMemberToOrder = ({ orderId, requestFrom, isUserExist }) => {
	const [usersByTypeList, setUsersByTypeList] = useState([]);
	const [addedMemberList, setAddedMemberList] = useState([]);
	const [showAddMemberModal, setShowAddMemberModal] = useState(false);
	const [searchUserSuggestions, setSearchUserSuggestions] = useState([]);
	const [searchUserText, setSearchUserText] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const dispatch = useDispatch();
	const PRO_PIC_DEFAULT = `${process.env.PUBLIC_URL}/images/pro_pic_default.svg`;
	const AddNewMemberButton = useRef(null);
	const AddNewMemberModal = useRef(null);
	const moreMember = 3;

	useEffect(() => {
		document.addEventListener('click', handleClickOutside, { capture: true });
		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	}, []);

	const handleClickOutside = (event) => {
		if (
			AddNewMemberModal.current &&
			!AddNewMemberModal.current.contains(event.target) &&
			AddNewMemberButton.current &&
			!AddNewMemberButton.current.contains(event.target)
		) {
			setShowAddMemberModal(false);
		}
	};

	const getUsersByTypes = async () => {
		dispatch(openLoader());
		let params = '?userTypes=PROJECT_MANAGER';
		await ApiClient.GET('getUsersByTypes', params)
			.then(({ data }) => {
				if (data) {
					dispatch(closeLoader());
					setUsersByTypeList(data);
				}
			})
			.catch(() => {
				dispatch(closeLoader());
				toastError('Something went wrong! Please try again.');
			});
	};

	const getAddedUsers = async () => {
		dispatch(openLoader());
		await ApiClient.GET('getOrderDetails', `${orderId}/members`)
			.then(({ data }) => {
				if (data) {
					dispatch(closeLoader());
					setAddedMemberList(data);
				}
			})
			.catch(() => {
				dispatch(closeLoader());
				toastError('Something went wrong! Please try again.');
			});
	};

	useEffect(() => {
		if (orderId) {
			getUsersByTypes();
			getAddedUsers();
		}
	}, [orderId]);

	let memberData = {
		usersByTypeList,
		searchUserText,
		searchUserSuggestions,
		memberList: addedMemberList
	};

	const getSearchSuggestions = async (userInput) => {
		setIsLoading(true);
		let params = `?email=${userInput}`;
		await ApiClient.GET('getUserSuggestions', params)
			.then(({ data }) => {
				setIsLoading(false);
				if (data) {
					setSearchUserSuggestions(data);
				}
			})
			.catch(({ response }) => {
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onChangeTeam = (e) => {
		setSearchUserText(e.target.value);
		getSearchSuggestions(e.target.value);
	};

	const addUserToOrder = async (user) => {
		setIsLoading(true);
		const body = user.id;
		let newAddedMembers = [...addedMemberList];
		await ApiClient.POST('addMemberToOrder', [body], `${orderId}`)
			.then(({ data }) => {
				setIsLoading(false);
				if (data && data.success) {
					if (addedMemberList && addedMemberList.length) {
						let flag = true;
						addedMemberList.forEach((item) => {
							if (item.id === user.id) {
								flag = false;
							}
						});
						if (flag) {
							toastSuccess(data.message);
							newAddedMembers = [...addedMemberList, user];
						} else {
							toastSuccess('Collection already shared');
						}
					} else {
						newAddedMembers = [user];
					}
					setAddedMemberList(newAddedMembers);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const removeUserFromOrder = async (user) => {
		setIsLoading(true);
		const body = user.id;
		let newAddedMembers = [...addedMemberList];
		await ApiClient.POST('removeMemberFromOrder', [body], `${orderId}`)
			.then(({ data }) => {
				if (data && data.success) {
					if (addedMemberList && addedMemberList.length) {
						newAddedMembers = addedMemberList.filter((item) => item.id !== user.id);
					}
					setAddedMemberList(newAddedMembers);
					setIsLoading(false);
					toastSuccess(data.message);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const renderIcon = (user) => {
		if (user.profilePicDocument && user.profilePicDocument.docUrl) {
			return (
				<img
					key={user.id}
					onClick={() => setShowAddMemberModal(!showAddMemberModal)}
					src={user.profilePicDocument.docUrl}
					alt=""
					data-toggle="tooltip"
					data-placement="top"
					title={user.name}
				/>
			);
		}
		return (
			<img
				key={user.id}
				onClick={() => setShowAddMemberModal(!showAddMemberModal)}
				src={PRO_PIC_DEFAULT}
				alt=""
				data-toggle="tooltip"
				data-placement="top"
				title={user.name}
			/>
		);
	};

	const renderMemberIcon = (userResponseList) => {
		if (userResponseList.length > moreMember) {
			return userResponseList.slice(0, moreMember).map((user) => {
				return renderIcon(user);
			});
		} else {
			return userResponseList.map((user) => {
				return renderIcon(user);
			});
		}
	};

	return (
		<div className="position-relative">
			<div className="px-0 add-buyer d-flex justify-content-end">
				<div className="position-relative">
					<div className="added-members">
						<div
							id="AddNewMember"
							ref={AddNewMemberButton}
							className={`add-new-member ${showAddMemberModal ? 'show' : ''}`}
						>
							<img
								src={`${process.env.PUBLIC_URL}/icons/add-people.svg`}
								alt="add"
								onClick={() => setShowAddMemberModal(!showAddMemberModal)}
							/>
						</div>

						{addedMemberList && addedMemberList ? (
							<>
								{renderMemberIcon(addedMemberList)}
								{addedMemberList.length > moreMember ? (
									<div className="more-people">
										<span onClick={() => setShowAddMemberModal(!showAddMemberModal)}>
											+{addedMemberList.length - moreMember}
										</span>
									</div>
								) : (
									<></>
								)}
							</>
						) : (
							<></>
						)}
					</div>
					<div
						className={`add-people-popup no-padding-popup shadow ${
							showAddMemberModal ? 'show' : ''
						}`}
						ref={AddNewMemberModal}
					>
						<div className="close-add-people mb-3 d-block d-sm-none">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="20.941"
								height="20.941"
								viewBox="0 0 20.941 20.941"
							>
								<g
									id="Group_22803"
									data-name="Group 22803"
									transform="translate(2489.29 -478.941)"
								>
									<line
										id="Line_153"
										data-name="Line 153"
										x2="25.615"
										transform="translate(-2487.875 480.355) rotate(45)"
										fill="none"
										stroke="#21242b"
										strokeLinecap="round"
										strokeWidth="2"
									/>
									<line
										id="Line_154"
										data-name="Line 154"
										x2="25.615"
										transform="translate(-2469.763 480.355) rotate(135)"
										fill="none"
										stroke="#21242b"
										strokeLinecap="round"
										strokeWidth="2"
									/>
								</g>
							</svg>
						</div>
						<LoaderComponent loading={isLoading}>
							<TeamMember
								memberData={memberData}
								onAddMember={addUserToOrder}
								onRemoveMember={removeUserFromOrder}
								onChange={onChangeTeam}
							/>
						</LoaderComponent>
					</div>
				</div>
			</div>
			{!isUserExist && requestFrom === 'PENDING_REQUEST' && (
				<span className="red regular-12 team-error">Project manager is not selected</span>
			)}
		</div>
	);
};

export default AddMemberToOrder;
